import * as inject from "@/components/inject";

/**
 * Make sure this window's pending debounced saves can be — and on unload,
 * are — flushed.
 *
 * Creates and provides the window's `pendingChangeFlushes` set (see
 * `inject.pendingChangeFlushes`), where components with debounced saves
 * (documents, AI prompts, assistant preferences, …) register their flush.
 * The returned `flushPendingChanges` drains it, pushing every pending save
 * immediately (Cmd+S in the main window). The set is also drained on
 * `beforeunload`, so edits still sitting in a debounce window when the
 * window closes (tab close, app quit) are pushed rather than lost. Best
 * effort: the flushes and the Vue microtask queue run before the page
 * unloads, so the persistence RPCs get posted, but the controller may not
 * finish writing if the process dies right after.
 *
 * Must be called synchronously in the root component's setup (it provides).
 * Owns the `beforeunload` listener: the caller must register the returned
 * `release` on its releaser.
 */
export function provideFlushPendingChanges(): {
  /** Push every registered pending debounced save immediately. */
  flushPendingChanges: () => void;
  release: () => void;
} {
  const callbacks: inject.PendingChangeFlushes = new Set();
  inject.pendingChangeFlushes(callbacks);
  const flushPendingChanges = () => callbacks.forEach((flush) => flush());
  window.addEventListener("beforeunload", flushPendingChanges);
  return {
    flushPendingChanges,
    release: () =>
      window.removeEventListener("beforeunload", flushPendingChanges),
  };
}
